import React, { useState, useEffect, useRef } from 'react';
import {
  Text,
  Box,
  Center,
  Divider,
  Button,
  Flex,
  theme,
} from '@chakra-ui/react';
import InfiniteScroll from 'react-infinite-scroll-component';
import PuffLoader from 'react-spinners/PuffLoader';
import { getRecentPosts } from 'controllers/engage';
import {
  removeResharedPosts,
  convertDateToFBTimestamp,
} from 'components/analytics/utils';
import { getAccount } from '../../../utils/sessionHelper';
import { textStyle } from 'theme/names';
import { CommentReplies } from './comment-replies-composer';
import { useSession } from 'next-auth/react';
import localforage from 'localforage';
import { FaAngleDown } from 'react-icons/fa';

export interface PostCommentBoxProps {
  postLimits: number;
  commentFetchLimists: number;
  username: any;
  idLinkedin: any;
  dashEntityUrn: any;
  isAuthenticated: boolean;
  isValidToken: boolean;
  idUser: any;
  dense?: boolean;
  hasLoadMoreButton?: boolean;
  onlyPostsWithComments?: boolean;
  hasReducedView?: boolean;
}

export const usePostCommentBox = ({
  postLimits = 5,
  commentFetchLimists = 30,
  username,
  idLinkedin,
  dashEntityUrn,
  isAuthenticated,
  isValidToken,
  idUser,
  dense = false,
  hasLoadMoreButton = false,
  hasReducedView = false,
  onlyPostsWithComments = false,
}: PostCommentBoxProps) => {
  const [recentPosts, setRecentPosts] = useState<any>([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState<boolean>(true);
  const [isLoadingComments, setIsLoadingComments] = useState<boolean>(false);
  const [hasMoreValues, setHasMoreValues] = useState<boolean>(true);
  const [userIsAuthenticated, setUserIsAuthenticated] =
    useState<boolean>(isAuthenticated);
  const [isTokenValid, setIsTokenValid] = useState<boolean>(isValidToken);
  const [nbCommentLimits, setNBCommentLimit] =
    useState<number>(commentFetchLimists);
  const [commentsData, setCommentsData] = useState<any>([]);
  const commentsFetcher = useRef<any>();
  const { data: session, status } = useSession() ?? {};
  const generatorComments = async function* () {
    setCommentsData([]); //re-init comments data on load.
    let postsWithComments = recentPosts?.filter((post: any) => {
      if (!!post?.numComments) return post;
    });

    postsWithComments = await removeResharedPosts(postsWithComments);
    postsWithComments = sortPosts(postsWithComments);
    postsWithComments = postsWithComments.slice(0, postLimits);

    for (let post of postsWithComments) {
      // console.log(post, 'comments - fn generator');
      yield await getRecentCommentsForPost(post);
    }
  };

  const fetchAddOrganizeComments = async () => {
    if (commentsFetcher.current) {
      const newCommentsData = await commentsFetcher.current.next();
      setHasMoreValues(!newCommentsData?.done);
      setCommentsData((prevCommentsData: any[]) => {
        if (!newCommentsData?.value) {
          return prevCommentsData;
        }
        return [...prevCommentsData, newCommentsData?.value];
      });
    }
  };

  const getRecentCommentsForPost = async (post) => {
    const returnedPostComments = {
      post,
      comments: [] as any,
    };

    try {
      setIsLoadingComments(true);

      if (!post?.id || !post.idShort) throw 'Post id or short id not found';

      const queryData = {
        linkedinShortPostId: post.idShort,
        idUser,
        dashEntityUrn: getAccount(session)?.dashEntityUrn,
        isInfluentLeader: true,
        planType: getAccount(session)?.subscription?.plan,
      };

      const res = await fetch(
        'https://us-central1-ez4cast.cloudfunctions.net/linkedinGrow-getPostComments',
        {
          method: 'POST',
          body: JSON.stringify(queryData),
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      const reply: any = await res.json();
      // console.log(reply, reply.results, 'reply')

      if (reply?.success) {
        // console.log(reply, 'comments reply');
        returnedPostComments.comments = [
          ...reply?.results.slice(0, nbCommentLimits),
        ];
        returnedPostComments.comments = returnedPostComments.comments.filter(
          (c: any) => {
            return post?.user?.dashEntityUrn == c?.dashEntityUrn ? false : true;
          }
        );
      } else {
        throw (
          "Couldn't fetch comments for post id: " +
          post?.id +
          ' Error: ' +
          reply?.error
        );
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoadingComments(false);
    }

    return returnedPostComments;
  };

  const getPosts = async () => {
    setRecentPosts([]);
    const retrieveCachedPosts: any = await localforage.getItem(`cachedPosts-${idUser}`);
    let nPosts = retrieveCachedPosts?.nPosts;

    if (
      Boolean(nPosts) === false ||
      Boolean(retrieveCachedPosts?.timeFetched) === false ||
      (Boolean(retrieveCachedPosts?.timeFetched) === true &&
        new Date().getTime() >
          retrieveCachedPosts.timeFetched.getTime() + 6 * (60 * 60 * 1000))
    ) {
      console.log(
        'Refreshing and fetching posts for RTC at:',
        retrieveCachedPosts?.timeFetched
      );
      nPosts = await getRecentPosts(
        postLimits,
        postLimits,
        username,
        idLinkedin,
        dashEntityUrn
      );
      nPosts = await checkRefreshPosts(nPosts);
      await localforage.setItem(`cachedPosts-${idUser}`, {
        timeFetched: new Date(),
        nPosts,
      });
    }

    if (onlyPostsWithComments) {
      nPosts = nPosts.filter((post: any) => {
        if (post && post?.numComments && post.numComments > 0) {
          return true;
        }

        return false;
      });
    }

    setRecentPosts([...nPosts]);
    setIsLoadingPosts(false);
  };

  const checkRefreshPosts = async (posts: Array<any> = []) => {
    if (!posts || !username) return posts;

    let newPosts: Array<any> = [];
    newPosts.push(...posts);

    const refreshPostInterval = 4 * (60 * 60 * 1000); //4hrs.
    const lastPostCreatedTimeStamp = posts?.[0]?.created_at.toDate()?.getTime();
    const checkAgainstTimeStamp = new Date(
      Date.now() - refreshPostInterval
    )?.getTime();

    if (lastPostCreatedTimeStamp < checkAgainstTimeStamp) {
      const queryData = {
        username,
      };

      const res = await fetch(
        'https://us-central1-ez4cast.cloudfunctions.net/linkedinGrow-fetchRefreshPosts',
        {
          method: 'POST',
          body: JSON.stringify(queryData),
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      const reply: any = await res.json();

      if (reply?.success) newPosts.push(...reply?.results);

      newPosts = newPosts.filter((post, index, self) => {
        const uniquePostIndex = self.findIndex((p) => {
          return p.idShort.toString() === post.idShort.toString();
        });
        return uniquePostIndex === index;
      });

      newPosts = sortPosts(newPosts);

      console.log('Post refresh from Reply to comment:', reply);
    }

    return newPosts;
  };

  const sortPosts = ([...posts]) => {
    const newPosts = posts.map((p) => {
      const isObjToDate =
        typeof p.created_at === 'object' &&
        Boolean(p.created_at.toDate) === false &&
        Boolean(p.created_at.seconds) === true;
      if (isObjToDate) {
        const newDate: any = new Date(
          p.created_at.seconds * 1000 + p.created_at.nanoseconds / 1000000
        );
        const preserveTS = convertDateToFBTimestamp(newDate);
        p = {
          ...p,
          created_at: preserveTS,
        };
      }
      return p;
    });

    newPosts.sort((postA: any, postB: any) => {
      const eTimePostA =
        typeof postA.created_at === 'string'
          ? new Date(postA.created_at).getTime()
          : postA.created_at.toDate().getTime();
      const eTimePostB =
        typeof postB.created_at === 'string'
          ? new Date(postB.created_at).getTime()
          : postB.created_at.toDate().getTime();
      if (eTimePostA < eTimePostB) return 1;
      if (eTimePostA > eTimePostB) return -1;
      return 0;
    });
    return newPosts;
  };

  const initGen = async () => {
    setCommentsData([]); //re-init comments data
    commentsFetcher.current = generatorComments();
    await fetchAddOrganizeComments();
  };

  useEffect(() => {
    setNBCommentLimit(commentFetchLimists);
  }, [commentFetchLimists]);

  useEffect(() => {
    if (
      username &&
      idLinkedin &&
      dashEntityUrn &&
      isLoadingPosts &&
      userIsAuthenticated &&
      isTokenValid
    )
      (async function () {
        await getPosts();
      })();
    else {
      console.log(
        'Missing params for fetching posts',
        username,
        idLinkedin,
        dashEntityUrn,
        isLoadingPosts,
        isTokenValid,
        userIsAuthenticated
      );
    }
  }, [
    username,
    idLinkedin,
    dashEntityUrn,
    isLoadingPosts,
    isTokenValid,
    userIsAuthenticated,
  ]);

  useEffect(() => {
    if (
      recentPosts?.length > 0 &&
      !isLoadingPosts &&
      userIsAuthenticated &&
      isTokenValid
    )
      (async function () {
        await initGen();
      })();
  }, [recentPosts, isTokenValid, userIsAuthenticated, isLoadingPosts]);

  useEffect(() => {
    setUserIsAuthenticated(isAuthenticated);
  }, [isAuthenticated]);

  useEffect(() => {
    setIsTokenValid(isValidToken);
  }, [isValidToken]);

  const refreshPosts = async () => {
    setIsLoadingPosts(true);
    setCommentsData([]);
    await getPosts();
  };

  const PostCommentBox = () => (
    <>
      <Box
        width="100%"
        pb={dense ? '' : ['300px', '300px', '300px', '300px', '450px']}
      >
        <InfiniteScroll
          dataLength={commentsData.length}
          next={fetchAddOrganizeComments}
          hasMore={!hasLoadMoreButton && hasMoreValues}
          loader={<LoadingPostsMessage />}
          endMessage={
            hasLoadMoreButton ? null : (
              <EndMessage postLength={commentsData?.length} />
            )
          }
          style={{
            position: 'relative',
            overflow: 'inherit',
            width: '100%',
          }}
        >
          {commentsData.length
            ? commentsData.map((commentPostobj, i) => {
                return (
                  <Box key={commentPostobj?.post?.id || i}>
                    <CommentReplies
                      data={commentPostobj}
                      loadingComments={false}
                      hasReducedView={hasReducedView}
                    />
                    {i < commentsData.length - 1 ? (
                      <Divider mt={4} borderColor={theme.colors.gray[300]} />
                    ) : null}
                  </Box>
                );
              })
            : null}
        </InfiniteScroll>
        {hasLoadMoreButton && isLoadingComments && <LoadingPostsMessage />}
        {!isLoadingPosts && !isLoadingComments && !commentsData?.length && (
          <Center
            mt={6}
            display={'flex'}
            w={'full'}
            alignContent={'center'}
            justifyContent={'center'}
            flexDir={'column'}
          >
            <Text
              textStyle={textStyle['body.medium.light']}
              fontSize="sm"
              mb={3}
            >
              No posts found
            </Text>
          </Center>
        )}
      </Box>
      {hasLoadMoreButton && hasMoreValues && (
        <Flex
          justifyContent="center"
          flexDirection="column"
          alignItems="center"
          mt={4}
        >
          <Button
            w="max-content"
            variant="secondary"
            rightIcon={<FaAngleDown />}
            onClick={() => {
              fetchAddOrganizeComments();
            }}
          >
            Show more
          </Button>
        </Flex>
      )}
    </>
  );

  return {
    isLoadingPosts,
    PostCommentBox,
    refreshPosts,
    recentPosts,
  };
};

const EndMessage = ({ postLength }) => {
  return (
    <Center mt={10} w={'full'} display={'flex'} flexDir={'column'}>
      <Text textAlign={'center'} w={'full'}>
        We have retrieved all comments from your{' '}
        {postLength == 0 ? 0 : postLength - 1} latest posts. Please check again
        tomorrow for your new posts. To see comments on other posts, use the
        "search by Post URL" method
      </Text>
      <Divider mb={0.5} mt={10} />
    </Center>
  );
};

const LoadingPostsMessage = ({}) => (
  <Center
    mt={6}
    display={'flex'}
    w={'full'}
    alignContent={'center'}
    justifyContent={'center'}
    flexDir={'column'}
  >
    <Text textStyle={textStyle['body.medium.light']} fontSize="sm" mb={3}>
      Loading post & comments...
    </Text>
    <PuffLoader loading={true} color={'#0A66C2'} />
  </Center>
);
